import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, Trash2, MessagesSquare, User, ArrowLeft, Image as ImageIcon, Hand, LogOut as Release, FileText, Settings2, Plus, Pencil, Check, Zap, Pin, Star, Flag, Ban, Forward, StickyNote, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { inputCls } from "./fields";
import { MessageMenu, MessageAttachments, ReplyQuote, Reactions, ReplyBar, PendingFiles, AttachMenu, usePendingFiles, useLongPress, VoiceRecorder, TypingBubble, Ticks, useTypingPing, MenuArrow, MessageInfo, menuFor, downloadMedia } from "../../components/chat/ChatParts";
import { uploadFile } from "../../lib/chatUpload";
import { customerMedia, GalleryStrip, GalleryPanel, Lightbox } from "./CustomerGallery";

const who = (r) => (r.sender === "visitor" ? "Customer" : r.sender_name || "Agent");

const SmsBadge = () => <span className="rounded bg-[#dcfce7] px-1.5 text-[10px] font-bold text-[#15803d]" data-testid="sms-badge">SMS</span>;

const AdminBubble = ({ m, onMenu, onZoom, readAt, sms }) => {
  const press = useLongPress(onMenu);
  const mine = m.sender === "admin";
  return (
    <div id={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="admin-chat-message">
      <div className="flex max-w-[75%] flex-col">
        <div className="group relative">
        <MenuArrow onOpen={onMenu} mine={mine} />
        <div {...press} className={`rounded-2xl px-4 py-2.5 text-[14px] ${m.reported ? "ring-2 ring-red-300" : ""} ${mine ? "rounded-br-sm bg-[#0e8c7f] text-white" : "rounded-tl-sm border border-[#e5f2ef] bg-white text-[#1f3a36]"}`} data-testid="admin-chat-bubble">
          {(m.pinned || m.starred || m.reported || m.forwarded) && (
            <div className={`mb-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold ${mine ? "text-white/80" : "text-[#6b7c79]"}`}>
              {m.forwarded && <span className="flex items-center gap-1 italic"><Forward size={11} />Forwarded</span>}
              {m.pinned && <span className="flex items-center gap-1" data-testid="msg-pinned-flag"><Pin size={11} />Pinned</span>}
              {m.starred && <span className="flex items-center gap-1" data-testid="msg-starred-flag"><Star size={11} className="fill-current" />Starred</span>}
              {m.reported && <span className="flex items-center gap-1 text-red-500" data-testid="msg-reported-flag"><Flag size={11} />Reported</span>}
            </div>
          )}
          {m.deleted ? (
            <div className={`flex items-center gap-1.5 italic ${mine ? "text-white/80" : "text-[#8aa29e]"}`} data-testid="admin-message-deleted"><Ban size={13} />This message was deleted</div>
          ) : (
            <>
              <ReplyQuote r={m.reply_to} mine={mine} label={m.reply_to ? who(m.reply_to) : ""} />
              <MessageAttachments m={m} onZoom={onZoom} mine={mine} />
              {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
            </>
          )}
          <div className={`mt-1 text-[10.5px] ${mine ? "text-white/70" : "text-[#8aa29e]"}`}>{m.edited_at && !m.deleted && <span className="italic" data-testid="msg-edited-label">Edited · </span>}{mine && m.sender_name ? `${m.sender_name} · ` : ""}{new Date(m.created_at).toLocaleString()}{mine && !m.deleted && (sms ? <span className="ml-1" data-testid="msg-sent-sms">· Sent by text</span> : <Ticks seen={!!readAt && m.created_at <= readAt} label={readAt && m.created_at <= readAt ? " Seen" : " Sent"} />)}</div>
        </div>
        </div>
        <Reactions reactions={m.reactions} mine={mine} />
      </div>
    </div>
  );
};

const NotesDialog = ({ open, onClose, session, reload, initial }) => {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(initial || ""); }, [open, initial]);
  const add = async () => {
    try { await api.post(`/admin/chats/${session.id}/notes`, { text }); setText(""); reload(); toast.success("Note saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (n) => { await api.delete(`/admin/chats/${session.id}/notes/${n.id}`); reload(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-[520px] overflow-y-auto" data-testid="notes-dialog">
        <DialogHeader><DialogTitle>Notes · {session?.name}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-[#5b6d6a]">Private team notes. Customers never see these.</p>
        <div className="space-y-2">
          {(session?.notes || []).map((n) => (
            <div key={n.id} className="flex items-start gap-2 rounded-xl bg-[#fff9e9] p-3 text-[13.5px]" data-testid="note-item">
              <div className="min-w-0 flex-1"><div className="whitespace-pre-wrap text-[#1f3a36]">{n.text}</div><div className="mt-1 text-[11px] text-[#8a9a97]">{n.by} · {new Date(n.at).toLocaleString()}</div></div>
              <button onClick={() => del(n)} className="text-red-500" aria-label="Delete note"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <textarea className={`${inputCls} min-h-[80px]`} placeholder="Write a note..." value={text} onChange={(e) => setText(e.target.value)} data-testid="note-input" />
        <button onClick={add} disabled={!text.trim()} className="sw-btn-primary !py-2 text-[14px] disabled:opacity-50" data-testid="note-save-btn"><Check size={15} />Save note</button>
      </DialogContent>
    </Dialog>
  );
};

const ForwardDialog = ({ msg, sessions, currentId, onClose }) => {
  const send = async (sid) => {
    try { await api.post(`/admin/chats/${currentId}/messages/${msg.id}/forward`, { target_sid: sid }); toast.success("Message forwarded"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!msg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-[440px] overflow-y-auto" data-testid="forward-dialog">
        <DialogHeader><DialogTitle>Forward to…</DialogTitle></DialogHeader>
        {sessions.filter((s) => s.id !== currentId).map((s) => (
          <button key={s.id} onClick={() => send(s.id)} className="flex w-full items-center gap-3 rounded-xl border border-[#e3f0ed] px-3 py-2.5 text-left hover:bg-[#f4fbf9]" data-testid="forward-target">
            <User size={16} className="text-[#0e8c7f]" /><span className="min-w-0 flex-1 truncate font-semibold text-[14px]">{s.name}</span><span className="truncate text-[12px] text-[#8a9a97]">{s.contact}</span>
          </button>
        ))}
      </DialogContent>
    </Dialog>
  );
};

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong";

const RepliesManager = ({ open, onClose, replies, reload }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/replies/${edit.id}`, edit);
      else await api.post("/admin/replies", edit);
      setEdit(null);
      reload();
      toast.success("Saved reply updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => { await api.delete(`/admin/replies/${r.id}`); reload(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-[600px] overflow-y-auto">
        <DialogHeader><DialogTitle>Saved replies</DialogTitle></DialogHeader>
        <p className="text-[13.5px] text-[#5b6d6a]">One-click answers for common pricing questions. Shared with the whole team.</p>
        <div className="space-y-2" data-testid="replies-list">
          {replies.map((r) => (
            <div key={r.id} className="flex items-start gap-3 rounded-xl border border-[#e3f0ed] p-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#0f2e2a]">{r.title}</div>
                <div className="text-[13px] text-[#5b6d6a]">{r.text}</div>
              </div>
              <button onClick={() => setEdit(r)} className="rounded-lg p-1.5 text-[#0e8c7f] hover:bg-[#e8f8f4]" aria-label="Edit"><Pencil size={15} /></button>
              <button onClick={() => del(r)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        {edit ? (
          <div className="space-y-3 rounded-xl bg-[#f4fbf9] p-4">
            <input className={inputCls} placeholder="Short title (e.g. Dexcom G7 price)" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="reply-title-input" />
            <textarea className={`${inputCls} min-h-[90px]`} placeholder="Reply text" value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} data-testid="reply-text-input" />
            <div className="flex gap-2">
              <button onClick={save} className="sw-btn-primary !py-2 text-[14px]" data-testid="reply-save-btn"><Check size={15} />Save</button>
              <button onClick={() => setEdit(null)} className="sw-btn-outline !py-1.5 text-[14px]">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setEdit({ title: "", text: "" })} className="flex items-center gap-1.5 rounded-xl border border-dashed border-[#0e8c7f] px-4 py-2.5 text-[14px] font-semibold text-[#0e8c7f] hover:bg-[#effaf7]" data-testid="add-reply-btn"><Plus size={16} />Add saved reply</button>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ChatsTab = ({ me }) => {
  const owner = me?.role === "admin";
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(() => new URLSearchParams(window.location.search).get("chat"));
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all");
  const [replies, setReplies] = useState([]);
  const [manage, setManage] = useState(false);
  const [team, setTeam] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [menu, setMenu] = useState(null);
  const [reply, setReply] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [fwd, setFwd] = useState(null);
  const [editing, setEditing] = useState(null);
  const [info, setInfo] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [lb, setLb] = useState(null);
  const endRef = useRef(null);
  const pending = usePendingFiles((m) => toast.error(m));
  const files = pending.items;
  const ping = useTypingPing(activeId ? `/admin/chats/${activeId}/typing` : null);
  const active = sessions.find((s) => s.id === activeId) || null;

  const loadSessions = useCallback(() => api.get("/admin/chats").then((r) => setSessions(r.data)).catch(() => {}), []);
  const loadMsgs = useCallback(() => {
    if (!activeId) return;
    api.get(`/admin/chats/${activeId}`).then((r) => setMsgs(r.data)).catch(() => {});
  }, [activeId]);
  const loadReplies = useCallback(() => api.get("/admin/replies").then((r) => setReplies(r.data)).catch(() => {}), []);

  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 4000); return () => clearInterval(t); }, [loadSessions]);
  useEffect(() => { loadMsgs(); const t = setInterval(loadMsgs, 3000); return () => clearInterval(t); }, [loadMsgs]);
  useEffect(() => { loadReplies(); if (owner) api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {}); }, [loadReplies, owner]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const lockedByOther = active?.assigned_to && active.assigned_to !== me?.email && !owner;
  const visible = sessions.filter((s) => filter === "all" || (filter === "mine" ? s.assigned_to === me?.email : !s.assigned_to));
  const counts = { all: sessions.length, mine: sessions.filter((s) => s.assigned_to === me?.email).length, open: sessions.filter((s) => !s.assigned_to).length };

  const send = async (e, voice) => {
    e?.preventDefault();
    if ((!text.trim() && !files.length && !voice) || !active) return;
    if (editing && !voice) {
      try {
        await api.patch(`/admin/chats/${active.id}/messages/${editing.id}/edit`, { text });
        if (active.channel === "sms") toast.message("Updated here only", { description: "The seller already received the original text message." });
        setEditing(null); setText(""); loadMsgs();
      } catch (err) { toast.error(errMsg(err)); }
      return;
    }
    setSending(true);
    try {
      const attachments = voice ? [await uploadFile(voice, {})] : await pending.uploadAll(active.id);
      await api.post(`/admin/chats/${active.id}`, { text: voice ? "" : text, attachments, reply_to: reply?.id });
      if (!voice) setText("");
      setReply(null);
      pending.reset();
      loadMsgs();
      loadSessions();
    } catch (err) {
      toast.error(errMsg(err));
      pending.setItems((prev) => prev.map((x) => ({ ...x, progress: null })));
    } finally {
      setSending(false);
    }
  };

  const action = async (path, body, msg) => {
    try {
      await api.post(`/admin/chats/${active.id}/${path}`, body);
      if (msg) toast.success(msg);
      loadSessions();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toQuote = async () => {
    try {
      const { data } = await api.post(`/admin/chats/${active.id}/to-quote`);
      toast.success(`Quote request created${data.images?.length ? ` with ${data.images.length} photo(s)` : ""}`, { description: "Find it under Quote Requests." });
      loadSessions();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    if (!active || !window.confirm("Delete this conversation?")) return;
    await api.delete(`/admin/chats/${active.id}`);
    toast.success("Conversation deleted");
    setActiveId(null);
    setMsgs([]);
    loadSessions();
  };

  const doAction = async (k) => {
    const m = menu?.m;
    const base = `/admin/chats/${active.id}/messages/${m.id}`;
    try {
      if (k === "reply") { setReply(m); setEditing(null); }
      if (k === "info") setInfo({ m, readAt: m.sender === "admin" && active.channel !== "sms" ? active.visitor_read_at || null : undefined, who: who(m) });
      if (k === "download") { await downloadMedia(m); return; }
      if (k === "edit") { setEditing(m); setReply(null); setText(m.text); return; }
      if (k === "copy") { await navigator.clipboard?.writeText(m.text || ""); toast.success("Copied"); }
      if (k === "forward") setFwd(m);
      if (k === "pin") await api.patch(base, { pinned: !m.pinned });
      if (k === "star") await api.patch(base, { starred: !m.starred });
      if (k === "report") { await api.patch(base, { reported: !m.reported }); toast.success(m.reported ? "Report removed" : "Message reported"); }
      if (k === "note") { setNoteDraft(m.text || ""); setNotesOpen(true); }
      if (k === "delete") { if (!window.confirm("Delete this message for everyone?")) return; await api.delete(base); }
      loadMsgs();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doReact = async (emoji) => {
    const m = menu?.m;
    await api.post(`/admin/chats/${active.id}/messages/${m.id}/react`, { emoji: m.reactions?.admin === emoji ? "" : emoji }).catch(() => {});
    loadMsgs();
  };
  const media = customerMedia(msgs);
  const replyTo = (m) => { setReply(m); setGalleryOpen(false); document.querySelector('[data-testid="admin-reply-input"]')?.focus(); };
  const pinned = msgs.filter((m) => m.pinned && !m.deleted);
  const menuLabels = menu ? { pin: menu.m.pinned ? "Unpin" : "Pin", star: menu.m.starred ? "Unstar" : "Star", report: menu.m.reported ? "Remove report" : "Report" } : {};

  const visitorPhotos = msgs.filter((m) => m.sender === "visitor").reduce((n, m) => n + (m.images?.length || 0), 0);

  return (
    <div className="grid h-[calc(100vh-150px)] min-h-[520px] overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white md:grid-cols-[330px_1fr]" data-testid="chats-tab">
      <div className={`${active ? "hidden md:flex" : "flex"} min-h-0 flex-col border-r border-[#eef5f3]`}>
        <div className="flex gap-1 border-b border-[#eef5f3] p-2.5">
          {[["all", "All"], ["mine", "Mine"], ["open", "Unclaimed"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`flex-1 rounded-lg px-2 py-2 text-[13px] font-semibold transition-colors ${filter === k ? "bg-[#0f2e2a] text-white" : "text-[#4b5d5a] hover:bg-[#f2f7f6]"}`} data-testid={`chat-filter-${k}`}>
              {l} <span className="opacity-70">({counts[k]})</span>
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {!visible.length && <div className="p-8 text-center text-[14px] text-[#6b7c79]"><MessagesSquare className="mx-auto mb-2 text-[#b6cbc7]" />No conversations here.</div>}
          {visible.map((s) => (
            <button key={s.id} onClick={() => { if (s.id !== activeId) { setMsgs([]); setLb(null); setReply(null); setEditing(null); } setActiveId(s.id); }} className={`flex w-full items-start gap-3 border-b border-[#f2f7f6] px-4 py-3.5 text-left transition-colors ${activeId === s.id ? "bg-[#effaf7]" : "hover:bg-[#fafdfc]"}`} data-testid="chat-session-item">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#d3f6ee] text-[#0e8c7f]"><User size={17} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-[#0f2e2a]">{s.name}</span>{s.channel === "sms" && <SmsBadge />}{s.lang === "es" && <span className="rounded bg-[#fff1d6] px-1.5 text-[10px] font-bold text-[#8a5a00]">ES</span>}
                  <span className="shrink-0 text-[11.5px] text-[#8a9a97]">{ago(s.updated_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex min-w-0 items-center gap-1 text-[13px] text-[#6b7c79]">{s.last_message?.startsWith("[") && <ImageIcon size={13} className="shrink-0" />}{s.visitor_typing ? <span className="truncate italic text-[#0e8c7f]" data-testid="session-typing">typing…</span> : <span className="truncate">{s.last_message}</span>}</span>
                  {s.unread_admin > 0 && <span className="ml-auto shrink-0 rounded-full bg-[#f59e0b] px-1.5 text-[11px] font-bold text-white">{s.unread_admin}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {s.assigned_to ? (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.assigned_to === me?.email ? "bg-[#dcf7ef] text-[#0b6f65]" : "bg-[#eef1f5] text-[#4b5563]"}`}>{s.assigned_to === me?.email ? "You" : s.assigned_name}</span>
                  ) : (
                    <span className="rounded-full bg-[#fff3dc] px-2 py-0.5 text-[11px] font-semibold text-[#a0620a]">Unclaimed</span>
                  )}
                  {s.quote_id && <span className="rounded-full bg-[#e0efff] px-2 py-0.5 text-[11px] font-semibold text-[#1d5fb3]">Quote</span>}
                  {s.video && <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-[#f1ebff] px-2 py-0.5 text-[11px] font-semibold text-[#5b3cc4]" data-testid="chat-video-badge"><Clapperboard size={11} />From video: {s.video}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={`${active ? "flex" : "hidden md:flex"} min-h-0 min-w-0 flex-col`}>
        {!active ? (
          <div className="flex flex-1 items-center justify-center text-[14.5px] text-[#6b7c79]">Select a conversation to reply</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eef5f3] px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <button onClick={() => setActiveId(null)} className="md:hidden" aria-label="Back"><ArrowLeft size={18} /></button>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate font-semibold text-[#0f2e2a]" data-testid="active-chat-name">{active.name}{active.channel === "sms" && <SmsBadge />}</div>
                  <div className="truncate text-[12.5px] text-[#6b7c79]">{active.channel === "sms" ? `Text messages · ${active.phone}${active.quote_code ? ` · Quote ${active.quote_code}` : ""}` : `${active.contact || "No contact given"} · ${active.page || "/"}${active.video ? ` · From video: ${active.video}` : ""}`}</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {owner ? (
                  <Select value={active.assigned_to || "none"} onValueChange={(v) => action("assign", { email: v === "none" ? null : v }, v === "none" ? "Chat unassigned" : "Chat assigned")}>
                    <SelectTrigger className="h-9 w-[160px] rounded-full text-[13px]" data-testid="chat-assign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {team.map((t) => <SelectItem key={t.email} value={t.email}>{t.name}{t.email === me?.email ? " (you)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : active.assigned_to === me?.email ? (
                  <button onClick={() => action("release", {}, "Chat released")} className="flex items-center gap-1.5 rounded-full border border-[#dcebe8] px-3 py-1.5 text-[13px] font-semibold text-[#4b5d5a] hover:bg-[#f4f8f7]" data-testid="chat-release-btn"><Release size={14} />Release</button>
                ) : !active.assigned_to ? (
                  <button onClick={() => action("claim", {}, "You claimed this chat")} className="flex items-center gap-1.5 rounded-full bg-[#0e8c7f] px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-[#0b7569]" data-testid="chat-claim-btn"><Hand size={14} />Claim</button>
                ) : null}
                <button onClick={toQuote} className="flex items-center gap-1.5 rounded-full border border-[#0e8c7f] px-3 py-1.5 text-[13px] font-semibold text-[#0e8c7f] hover:bg-[#effaf7]" title="Create a quote request from this chat with all photos" data-testid="chat-to-quote-btn">
                  <FileText size={14} />{active.quote_id ? "New quote" : "Make quote"}{visitorPhotos > 0 && <span className="rounded-full bg-[#0e8c7f] px-1.5 text-[11px] text-white">{visitorPhotos}</span>}
                </button>
                <button onClick={() => { setNoteDraft(""); setNotesOpen(true); }} className="flex items-center gap-1.5 rounded-full border border-[#dcebe8] px-3 py-1.5 text-[13px] font-semibold text-[#4b5d5a] hover:bg-[#f4f8f7]" data-testid="chat-notes-btn"><StickyNote size={14} />Notes{active.notes?.length ? ` (${active.notes.length})` : ""}</button>
                <button onClick={remove} className="rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label="Delete chat"><Trash2 size={16} /></button>
              </div>
            </div>
            {active.channel === "sms" && (
              <div className={`px-5 py-2 text-[12.5px] ${active.sms_opted_out ? "bg-red-50 text-red-700" : "bg-[#ecfbf3] text-[#0b6f45]"}`} data-testid="sms-chat-banner">
                {active.sms_opted_out ? "This seller replied STOP. Texts are blocked until they reply START." : "Your replies here are sent as text messages to this seller. Photos go as picture messages; files and voice notes as links."}
              </div>
            )}
            {lockedByOther && (
              <div className="bg-[#fff8e8] px-4 py-2 text-[13px] font-medium text-[#8a5a06]" data-testid="chat-locked-banner">Claimed by {active.assigned_name}. Only they or the main admin can reply.</div>
            )}
            <GalleryStrip items={media} onOpen={setLb} onPanel={() => setGalleryOpen(true)} />
            {pinned.length > 0 && (
              <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-[#eef5f3] bg-[#f6fbfa] px-4 py-2" data-testid="pinned-bar">
                <Pin size={14} className="mt-1 shrink-0 text-[#0e8c7f]" />
                {pinned.map((m) => (
                  <button key={m.id} onClick={() => document.getElementById(`msg-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="max-w-[220px] shrink-0 truncate rounded-full border border-[#d6efe9] bg-white px-3 py-1 text-[12.5px] text-[#1f3a36]" data-testid="pinned-item">{m.text || "[attachment]"}</button>
                ))}
              </div>
            )}
            <div className="flex-1 space-y-3 overflow-y-auto bg-[#fbfdfd] px-5 py-5" data-testid="admin-chat-messages">
              {msgs.map((m) => <AdminBubble key={m.id} m={m} onZoom={setZoom} onMenu={(x, y) => !m.deleted && setMenu({ x, y, m })} readAt={active.channel === "sms" ? null : active.visitor_read_at} sms={active.channel === "sms"} />)}
              {active.visitor_typing && <TypingBubble label={`${active.name || "Customer"} is typing…`} />}
              <div ref={endRef} />
            </div>
            <div className="flex items-center gap-2 border-t border-[#eef5f3] px-4 pt-3">
              <Zap size={14} className="shrink-0 text-[#f59e0b]" />
              <div className="no-scrollbar flex flex-1 gap-2 overflow-x-auto" data-testid="saved-replies-bar">
                {replies.map((r) => (
                  <button key={r.id} onClick={() => setText(r.text)} disabled={lockedByOther} title={r.text} className="shrink-0 rounded-full border border-[#d6efe9] px-3 py-1 text-[12.5px] font-medium text-[#0b6f65] hover:bg-[#effaf7] disabled:opacity-40" data-testid="saved-reply-chip">{r.title}</button>
                ))}
              </div>
              <button onClick={() => setManage(true)} className="shrink-0 rounded-full p-1.5 text-[#5b6d6a] hover:bg-[#f2f7f6]" title="Manage saved replies" aria-label="Manage saved replies" data-testid="manage-replies-btn"><Settings2 size={16} /></button>
            </div>
            <div className="px-4 pt-3 empty:hidden">
              <ReplyBar reply={reply || editing} label={editing ? `Edit message${active.channel === "sms" ? " (seller won't see the change)" : ""}` : reply ? `Replying to ${who(reply)}` : ""} onCancel={() => { setReply(null); if (editing) { setEditing(null); setText(""); } }} />
              <PendingFiles items={files} onRemove={pending.remove} />
            </div>
            <form onSubmit={send} className="relative flex items-end gap-2 p-4">
              <AttachMenu onFiles={pending.add} disabled={lockedByOther || files.length >= 5 || sending} />
              <textarea onPaste={pending.onPaste} value={text} disabled={lockedByOther} onChange={(e) => { setText(e.target.value); ping(); }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} rows={1} placeholder={lockedByOther ? "Claimed by another agent" : "Type a reply... (sending auto-claims an unclaimed chat)"} className="max-h-28 flex-1 resize-none rounded-2xl border border-[#dcebe8] px-4 py-2.5 text-[14px] outline-none focus:border-[#0e8c7f] disabled:bg-[#f4f6f6]" data-testid="admin-reply-input" />
              {!text.trim() && !files.length ? <VoiceRecorder disabled={lockedByOther || sending} onError={(m) => toast.error(m)} onRecorded={(f) => send(null, f)} /> :
              <button type="submit" disabled={lockedByOther || sending || (!text.trim() && !files.length)} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0e8c7f] text-white hover:bg-[#0b7569] disabled:opacity-50" data-testid="admin-reply-send" aria-label="Send">{sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</button>}
            </form>
          </>
        )}
      </div>
      <MessageInfo info={info} onClose={() => setInfo(null)} />
      <MessageMenu menu={menu} actions={menu ? menuFor(menu.m, menu.m.sender === "admin", ["forward", "pin", "star", "note", "report", "delete"]) : []} labels={menuLabels}
        onAction={doAction} onReact={doReact} onClose={() => setMenu(null)} />
      {active && <NotesDialog open={notesOpen} onClose={() => setNotesOpen(false)} session={active} reload={loadSessions} initial={noteDraft} />}
      <GalleryPanel open={galleryOpen} items={media} name={active?.name || "Customer"} onClose={() => setGalleryOpen(false)} onOpen={setLb} onReply={replyTo} />
      <Lightbox items={media} index={lb} setIndex={setLb} onReply={replyTo} />
      <ForwardDialog msg={fwd} sessions={sessions} currentId={active?.id} onClose={() => setFwd(null)} />
      <RepliesManager open={manage} onClose={() => setManage(false)} replies={replies} reload={loadReplies} />
      {zoom && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={() => setZoom(null)}>
          <img src={zoom} alt="Attachment" className="max-h-[90vh] max-w-full rounded-xl" />
          <a href={zoom} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute bottom-6 rounded-full bg-white px-4 py-2 text-[13.5px] font-semibold text-[#0f2e2a]">Open full size</a>
        </div>
      )}
    </div>
  );
};

export default ChatsTab;
